import { reactive, watch } from 'vue';
import { watchOnce } from '@vueuse/core';

import { cookie } from '@libs/cookie';

import {
  isFeatureToggleActive,
  getMultiExperimentOverrides,
  FeaturePrefix,
} from '../customFeatureToggles';

export type StatsigExperimentName =
  | 'suggested_hotel_in_hotel_selection'
  | 'suggested_flight_in_flights_selection'
  | 'suggested_ferry_in_ferry_selection'
  | `request_user_location_only_when_no_meta_hotel`
  | 'hotel_selection_alternative_card_desktop'
  | 'overview_alternative_config';

export type StatsigMultiExperimentName =
  | 'placeholder_multi'
  | 'hotel_selection_alternative_card_mobile'
  | 'hotel_horizontal_filters_alignment';

interface StatsigExperimentState {
  value: boolean;
  isLoaded: boolean;
}

// To correctly override test value for E2E tests, multiversion experiments should always have "control" as a default value, otherwise there is a risk that experiment will be loaded before test override is applied, which may cause test flakiness.
// This is not an issue for boolean experiments, as their default value is false, which is the same as control group.
interface StatsigExperimentMultiState {
  value: string;
  isLoaded: boolean;
}

type StatsigExperiments = Record<StatsigExperimentName, StatsigExperimentState>;

type StatsigMultiExperiments = Record<
  StatsigMultiExperimentName,
  StatsigExperimentMultiState
>;

export const FORCE_ALL_EXPERIMENTS_TO_CONTROL_COOKIE =
  'force_all_experiments_to_control';

export const statsigExperiments = reactive<StatsigExperiments>({
  suggested_hotel_in_hotel_selection: { value: false, isLoaded: false },
  suggested_flight_in_flights_selection: { value: false, isLoaded: false },
  suggested_ferry_in_ferry_selection: { value: false, isLoaded: false },
  request_user_location_only_when_no_meta_hotel: {
    value: false,
    isLoaded: false,
  },
  hotel_selection_alternative_card_desktop: { value: false, isLoaded: false },
  overview_alternative_config: { value: false, isLoaded: false },
});

const statsigMultiExperiments = reactive<StatsigMultiExperiments>({
  placeholder_multi: { value: 'placeholder_multi', isLoaded: false },
  hotel_selection_alternative_card_mobile: {
    value: 'control',
    isLoaded: false,
  },
  hotel_horizontal_filters_alignment: {
    value: 'control',
    isLoaded: false,
  },
});

export function assignExperimentValue(
  experimentName: StatsigExperimentName,
  value: boolean
): void {
  statsigExperiments[experimentName].value = value;
  statsigExperiments[experimentName].isLoaded = true;
}

export function assignMultiExperimentValue(
  multiExperimentName: StatsigMultiExperimentName,
  value: string
): void {
  statsigMultiExperiments[multiExperimentName].value = value;
  statsigMultiExperiments[multiExperimentName].isLoaded = true;
}

// On Statsig initialization failure the per-experiment assignExperimentValue
// calls never run, so isLoaded stays false and isExperimentAsync would never
// resolve (its resolver waits for isLoaded via watch). Flip isLoaded on every
// boolean experiment so consumers resolve to the default (false = control) and
// degrade gracefully instead of stalling. Already-loaded experiments keep their
// value — only isLoaded is touched. Call this from the Statsig init catch block.
export function markUnloadedExperimentsAsControl(): void {
  (Object.keys(statsigExperiments) as StatsigExperimentName[]).forEach(
    (experimentName) => {
      statsigExperiments[experimentName].isLoaded = true;
    }
  );
  (
    Object.keys(statsigMultiExperiments) as StatsigMultiExperimentName[]
  ).forEach((multiExperimentName) => {
    statsigMultiExperiments[multiExperimentName].isLoaded = true;
  });
}

export async function isExperimentAsync(
  experimentName: StatsigExperimentName
): Promise<boolean> {
  return new Promise((resolve) => {
    if (
      cookie.get(FORCE_ALL_EXPERIMENTS_TO_CONTROL_COOKIE)?.toLowerCase() ===
      'true'
    ) {
      resolve(false);
      return;
    }

    if (isFeatureToggleActive(experimentName)) {
      resolve(true);
    } else if (isFeatureToggleActive(`!${experimentName}`)) {
      resolve(false);
    } else if (statsigExperiments[experimentName].isLoaded) {
      resolve(statsigExperiments[experimentName].value);
    } else {
      watch(
        () => statsigExperiments[experimentName].isLoaded,
        () => resolve(statsigExperiments[experimentName].value),
        { once: true }
      );
    }
  });
}

export async function isMultiExperimentAsync(
  multiExperimentName: StatsigMultiExperimentName
): Promise<string> {
  return new Promise((resolve) => {
    if (
      cookie.get(FORCE_ALL_EXPERIMENTS_TO_CONTROL_COOKIE)?.toLowerCase() ===
      'true'
    ) {
      resolve('control');
      return;
    }

    const overrides = getMultiExperimentOverrides(FeaturePrefix.TripPlanner);
    const urlOverride = overrides.get(multiExperimentName);
    if (urlOverride !== undefined) {
      resolve(urlOverride);
      return;
    }

    if (statsigMultiExperiments[multiExperimentName].isLoaded) {
      resolve(statsigMultiExperiments[multiExperimentName].value);
    } else {
      watchOnce(
        () => statsigMultiExperiments[multiExperimentName].isLoaded,
        () => resolve(statsigMultiExperiments[multiExperimentName].value)
      );
    }
  });
}
